/* Customer reviews carousel (sections/fj-reviews.liquid, Jenny 2026-10-10).
   The track is a native scroll-snap row, so phones swipe for free; the
   arrows scroll it by one card. On phones the card in view is marked
   .is-current and the others fade, so the peeking next card reads as
   "there's more".

   It LOOPS (Jenny 2026-10-10): a copy of every review sits before and after
   the real ones, so past the last review the first one is already there.
   Once the track comes to rest on a copy, it jumps (instantly, the copy and
   the real card look the same) to the real card, so the loop never runs
   out. The copies are hidden from screen readers and from the Tab order.
   Fewer than four reviews do not loop; the arrows grey out at the ends.

   It also SLIDES BY ITSELF (Jenny 2026-10-10): one card every 7s (time to
   skim a four-line review), starting as soon as the page loads. It pauses
   while the pointer rests on the cards or a card has keyboard focus, and
   while the tab is hidden. An arrow click, swipe or wheel stops it; it
   picks up again 10s after the last one. Never with reduced motion, and
   only when it loops. */
(() => {
  if (customElements.get('fj-reviews')) return;

  const MIN_LOOP = 4;
  const AUTO_EVERY = 7000;
  const AUTO_RESUME = 10000;

  class FjReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.fj-rv__track');
      this.prev = this.querySelector('.fj-rv__arrow--prev');
      this.next = this.querySelector('.fj-rv__arrow--next');
      const real = [...this.querySelectorAll('.fj-rv__slide')];
      if (!this.track || !real.length) return;

      this.count = real.length;
      this.loop = this.count >= MIN_LOOP;
      if (this.loop) {
        const copy = (slide) => {
          const c = slide.cloneNode(true);
          c.classList.add('fj-rv__slide--copy');
          c.setAttribute('aria-hidden', 'true');
          c.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
          return c;
        };
        this.track.prepend(...real.map(copy));
        this.track.append(...real.map(copy));
      }
      this.slides = [...this.track.children];
      this.first = this.loop ? this.count : 0; // index of the first real card
      this.target = null;

      this.querySelectorAll('.fj-rv__arrow').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.step(Number(btn.dataset.dir));
          this.hold();
        });
      });
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      // A swipe or wheel takes over from an arrow's glide.
      ['pointerdown', 'wheel', 'touchstart'].forEach((type) => {
        this.track.addEventListener(type, () => {
          this.target = null;
          this.hold();
        }, { passive: true });
      });
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', () => this.settle());
      }
      window.addEventListener('resize', () => {
        this.jumpTo(this.nearest());
        this.queue();
      });

      this.jumpTo(this.first);
      this.update();
      this.setUpAuto();
    }

    setUpAuto() {
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoOn = this.loop && !calm.matches;
      if (!this.autoOn) return;
      // Resting on the cards (reading) or tabbing through them pauses it.
      this.addEventListener('mouseenter', () => { this.resting = true; this.schedule(); });
      this.addEventListener('mouseleave', () => { this.resting = false; this.schedule(); });
      // Keyboard focus only: a clicked arrow keeps focus in Chrome, which
      // would otherwise pause it until the shopper clicked elsewhere.
      this.addEventListener('focusin', (e) => {
        this.focused = e.target.matches(':focus-visible');
        this.schedule();
      });
      this.addEventListener('focusout', (e) => {
        if (this.contains(e.relatedTarget)) return;
        this.focused = false;
        this.schedule();
      });
      document.addEventListener('visibilitychange', () => this.schedule());
      calm.addEventListener('change', () => {
        this.autoOn = !calm.matches;
        this.schedule();
      });
      this.schedule();
    }

    // (Re)arm the next automatic step, unless something is holding it.
    schedule(wait = AUTO_EVERY) {
      clearTimeout(this.autoTimer);
      if (!this.autoOn || this.resting || this.focused || document.hidden) return;
      // An arrow or swipe still holds it for the rest of its 10s.
      wait = Math.max(wait, (this.heldUntil || 0) - Date.now());
      this.autoTimer = setTimeout(() => {
        this.step(1);
        this.schedule();
      }, wait);
    }

    // The shopper took over: wait a while before sliding again.
    hold() {
      this.heldUntil = Date.now() + AUTO_RESUME;
      this.schedule();
    }

    // Scroll position that snaps slide i to the track's start.
    offsetOf(i) {
      const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      return this.slides[i].offsetLeft - this.track.offsetLeft - pad;
    }

    // Index of the slide whose left edge is nearest the snap start.
    nearest() {
      const x = this.track.scrollLeft;
      let best = 0;
      let dist = Infinity;
      this.slides.forEach((_, i) => {
        const d = Math.abs(this.offsetOf(i) - x);
        if (d < dist) { dist = d; best = i; }
      });
      return best;
    }

    // Moves without animation (CSS asks for smooth scrolling, so it is
    // switched off for the one write).
    jumpTo(i) {
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = this.offsetOf(i);
      this.track.style.scrollBehavior = '';
    }

    step(dir) {
      let from = this.target ?? this.nearest();
      if (this.loop) {
        // Heading off the copies: hop to the matching real card first.
        // Seamless, they look the same, even mid-glide.
        const next = from + dir;
        if (next < this.first || next >= this.first + this.count) {
          const shift = next < this.first ? this.count : -this.count;
          const x = this.track.scrollLeft - this.offsetOf(from);
          from += shift;
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollLeft = this.offsetOf(from) + x;
          this.track.style.scrollBehavior = '';
        }
      }
      const to = Math.max(0, Math.min(this.slides.length - 1, from + dir));
      this.target = to;
      this.track.scrollTo({ left: this.offsetOf(to), behavior: 'smooth' });
    }

    onScroll() {
      this.queue();
      // Browsers without a scrollend event: treat a short pause as the end.
      if (!('onscrollend' in window)) {
        clearTimeout(this.idle);
        this.idle = setTimeout(() => this.settle(), 150);
      }
    }

    // The track came to rest: if it rests on a copy, jump to the real card.
    // An arrow's glide is not over until it reaches its card: the instant
    // hop in step() fires its own scrollend mid-glide, and treating that as
    // rest jumped the track back to where it started.
    settle() {
      if (this.target !== null) {
        if (Math.abs(this.track.scrollLeft - this.offsetOf(this.target)) > 2) return;
        this.target = null;
      }
      if (!this.loop) return;
      const i = this.nearest();
      if (i < this.first) this.jumpTo(i + this.count);
      else if (i >= this.first + this.count) this.jumpTo(i - this.count);
      this.update();
    }

    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      let current = this.nearest();
      if (!this.loop) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const x = this.track.scrollLeft;
        if (this.prev) this.prev.disabled = x <= 2;
        if (this.next) this.next.disabled = x >= max - 2;
        if (x >= max - 2) current = this.slides.length - 1;
      }
      this.slides.forEach((slide, i) => slide.classList.toggle('is-current', i === current));
    }
  }

  customElements.define('fj-reviews', FjReviews);
})();
